<template>
  <div>
    <NewFormOfPaymentDialog
      v-if="showNewFormOfPaymentDialog"
      @closed="closed"
      @created="createdFormOfPayment"
    />
    <ExtendedView :tabs="tabs">
      <template v-slot:list="{ item }">
        <FormOfPaymentListServerSide
          v-if="item.key === 'active'"
          ref="activeList"
          :load-items-callback="getActiveFormOfPayments"
          :load-item-details-callback="loadFormOfPaymentDetails"
          :call-to-actions="callToActions"
          :fixed-filters="activeFilters"
          @selectFormOfPayment="selectFormOfPayment"
        />
        <FormOfPaymentListServerSide
          v-if="item.key === 'inactive'"
          ref="inactiveList"
          :load-items-callback="getInactiveFormOfPayments"
          :load-item-details-callback="loadFormOfPaymentDetails"
          :call-to-actions="callToActions"
          :fixed-filters="inactiveFilters"
          @selectFormOfPayment="selectFormOfPayment"
        />
      </template>
      <template slot="profile">
        <FormOfPaymentProfile
          v-if="selectedFormOfPayment !== null"
          :form-of-payment="selectedFormOfPayment"
          @updated="selectedFormOfPayment = null"
        />
      </template>
    </ExtendedView>
  </div>
</template>

<script>
import ExtendedView from '@/components/common/ExtendedView'
import FormOfPaymentListServerSide from '@/components/formsOfPayment/FormOfPaymentListServerSide'
import NewFormOfPaymentDialog from '@/components/formsOfPayment/NewFormOfPaymentDialog'
import FormOfPaymentProfile from '@/components/formsOfPayment/FormOfPaymentProfile'

export default {
  name: 'FormOfPaymentsServerSide',
  components: {
    NewFormOfPaymentDialog,
    FormOfPaymentListServerSide,
    FormOfPaymentProfile,
    ExtendedView
  },
  data() {
    return {
      callToActions: [
        {
          title: this.$t('addGiftcard'),
          callback: this.toggleNewFormOfPaymentDialog
        }
      ],
      showNewFormOfPaymentDialog: false,
      selectedFormOfPayment: null,
      exporting: false,
      activeFilters: [
        {
          key: 'active',
          value: true,
          operator: 'equals',
          type: 'boolean'
        }
      ],
      inactiveFilters: [
        {
          key: 'active',
          value: false,
          operator: 'equals',
          type: 'boolean'
        }
      ]
    }
  },
  methods: {
    getActiveFormOfPayments({ limit, skip, conditions, search }) {
      if (conditions.find((condition) => condition.key === 'active' || condition.property === 'active')) {
        conditions = conditions.filter((condition) => condition.key !== 'active' && condition.property !== 'active')
      }

      // Combine fixed filters with conditions
      const queryConditions = this.getQueryConditionsFromFilters(this.activeFilters)
      const allConditions = [...queryConditions, ...(conditions || [])]
      return this.$store.dispatch('findFormOfPayments', {
        conditions: allConditions,
        search: search,
        limit,
        skip
        // Note: search parameter would need backend support
        // For now, search can be handled via conditions if needed
      })
    },
    getInactiveFormOfPayments({ limit, skip, conditions, search }) {
      if (conditions.find((condition) => condition.key === 'active' || condition.property === 'active')) {
        conditions = conditions.filter((condition) => condition.key !== 'active' && condition.property !== 'active')
      }

      // Combine fixed filters with conditions
      const queryConditions = this.getQueryConditionsFromFilters(this.inactiveFilters)
      const allConditions = [...queryConditions, ...(conditions || [])]
      return this.$store.dispatch('findFormOfPayments', {
        conditions: allConditions,
        search: search,
        limit,
        skip
        // Note: search parameter would need backend support
        // For now, search can be handled via conditions if needed
      })
    },
    getQueryConditionsFromFilters(filters) {
      const queryConditions = JSON.parse(JSON.stringify(filters))

      for (let i in queryConditions) {
        queryConditions[i].property = queryConditions[i].key

        if (queryConditions[i].value === true || queryConditions[i].value === false) {
          queryConditions[i].booleanValue = queryConditions[i].value
          delete queryConditions[i].value
        }

        delete queryConditions[i].key
        delete queryConditions[i].label
        delete queryConditions[i].type
      }

      return queryConditions
    },
    loadFormOfPaymentDetails(item) {
      const uuid = item && item.uuid ? item.uuid : null
      if (!uuid) {
        console.error('Item does not have a UUID:', item)
        return Promise.resolve(null)
      }

      return this.$store
        .dispatch('getFormOfPayment', { uuid })
        .then((result) => {
          if (Array.isArray(result)) {
            return result.length > 0 ? result[0] : null
          }
          return result || null
        })
        .catch((error) => {
          console.error('Error loading form of payment details:', error)
          return null
        })
    },
    selectFormOfPayment(formOfPayment) {
      this.selectedFormOfPayment = formOfPayment
    },
    async exportFormOfPayments() {
      if (this.exporting) return

      this.exporting = true
      try {
        // Get the currently active tab
        const activeTab = this.selectedAppBarTab || 'active'
        const listRef = activeTab === 'active' ? this.$refs.activeList : this.$refs.inactiveList

        if (listRef && listRef.exportAllItems) {
          await listRef.exportAllItems()
        }
      } catch (error) {
        console.error('Error exporting form of payments:', error)
      } finally {
        this.exporting = false
      }
    },
    toggleNewFormOfPaymentDialog() {
      this.showNewFormOfPaymentDialog = !this.showNewFormOfPaymentDialog
    },
    closed() {
      this.showNewFormOfPaymentDialog = false
    },
    createdFormOfPayment() {
      this.$store.commit('updateAppBarTabs', this.tabs)
      // Refresh the active list to show the newly created form of payment
      this.$nextTick(() => {
        const activeTab = this.selectedAppBarTab || 'active'
        const listRef = activeTab === 'active' ? this.$refs.activeList : this.$refs.inactiveList
        if (listRef && listRef.refresh) {
          listRef.refresh()
        }
      })
    },
    updateAppBarExport() {
      this.$store.commit('updateAppBarExport', {
        callback: this.exportFormOfPayments,
        loading: this.exporting
      })
    }
  },
  computed: {
    tabs() {
      return [
        {
          title: this.$t('active'),
          key: 'active',
          badge: {
            color: 'green',
            content: null // Server-side: count would need to come from API
          }
        },
        {
          title: this.$t('inactive'),
          key: 'inactive',
          badge: {
            color: 'red',
            content: null // Server-side: count would need to come from API
          }
        }
      ]
    },
    selectedAppBarTab() {
      return this.$store.state.selectedAppBarTab
    }
  },
  watch: {
    exporting(newValue) {
      this.updateAppBarExport()
    }
  },
  mounted() {
    this.$store.commit('updateAppBarTabs', this.tabs)
    this.$store.commit('updateSelectedAppBarTab', this.tabs[0].key)
  },
  created() {
    this.updateAppBarExport()
  }
}
</script>
